import type { CSSProperties } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { focusRing } from "@ui/tokens/interaction";
import { contrastRatio, mixSrgb, parseHex } from "@ui/tokens/classCover/colorMix";

interface ColorChoiceProps {
  colors: readonly string[];
  value: string;
  onChange: (hex: string) => void;
  label: string;
}

const WHITE = { r: 255, g: 255, b: 255 };
const MIN_ICON_CONTRAST = 3;

const checkInkOf = (hex: string): string =>
  contrastRatio(WHITE, parseHex(hex)) >= MIN_ICON_CONTRAST ? "text-white" : "text-ink";

const DEEPENER = "#14161c";

const edgeOf = (hex: string): CSSProperties =>
  ({ background: hex, "--swatch-edge": mixSrgb(hex, 0.5, DEEPENER) }) as CSSProperties;

const swatch = "relative inline-flex items-center justify-center w-8 h-8 rounded-full transition duration-hover";

/**
 * The one way to pick a colour: a row of proposals, then a free choice that turns into the colour
 * it picked. Classes and categories both ask for a colour, and two different pickers for the same
 * question is how a teacher learns that the product is not one thing.
 */
export const ColorChoice = ({ colors, value, onChange, label }: ColorChoiceProps) => {
  const current = value.toUpperCase();
  const proposed = colors.map((entry) => entry.toUpperCase());
  const isCustom = !proposed.includes(current);

  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2.5">
      {colors.map((entry) => {
        const selected = current === entry.toUpperCase();
        return (
          <button
            key={entry}
            type="button"
            aria-label={`Couleur ${entry}`}
            aria-pressed={selected}
            onClick={() => onChange(entry)}
            style={edgeOf(entry)}
            className={`${swatch} hover:scale-110 ${selected ? "shadow-shape-active" : "shadow-swatch-pick"} ${focusRing}`}
          >
            {selected && <CheckIcon className={`w-3.5 h-3.5 ${checkInkOf(entry)}`} strokeWidth={3} aria-hidden="true" />}
          </button>
        );
      })}
      <label
        title="Couleur personnalisée"
        style={{
          background: isCustom
            ? value
            : "conic-gradient(#ef4444, #eab308, #22c55e, #06b6d4, #6366f1, #d946ef, #ef4444)",
        }}
        className={`${swatch} cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink ${
          isCustom ? "shadow-shape-active" : "shadow-swatch-ring hover:scale-110"
        }`}
      >
        {isCustom && <CheckIcon className={`w-3.5 h-3.5 ${checkInkOf(value)}`} strokeWidth={3} aria-hidden="true" />}
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Couleur personnalisée"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </label>
      <span className="ml-1 font-numeric text-control-sm uppercase tracking-wide text-ink-help">{current}</span>
    </div>
  );
};
